import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { digitalHumansApi } from '@/services/api';
import { useOrgContextStore } from '@/stores/orgContextStore';
import type {
  DigitalHumanCreateFormData,
  DigitalHumanCreateRequest,
  DigitalHumanListParams,
  DigitalHumanUpdateFormData,
  DigitalHumanUpdateRequest,
} from '@/types/digital-human';

const digitalHumanKeys = {
  all: ['digital-humans'] as const,
  context: (activeOrgId: string | null) =>
    [...digitalHumanKeys.all, activeOrgId ?? 'personal'] as const,
  lists: (activeOrgId: string | null) =>
    [...digitalHumanKeys.context(activeOrgId), 'list'] as const,
  list: (activeOrgId: string | null, params?: DigitalHumanListParams) =>
    [...digitalHumanKeys.lists(activeOrgId), params] as const,
  details: (activeOrgId: string | null) =>
    [...digitalHumanKeys.context(activeOrgId), 'detail'] as const,
  detail: (activeOrgId: string | null, digitalHumanId: string) =>
    [...digitalHumanKeys.details(activeOrgId), digitalHumanId] as const,
};

export function invalidateDigitalHumans(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: digitalHumanKeys.all });
}

export function useDigitalHumans(params?: DigitalHumanListParams, options?: { enabled?: boolean }) {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);

  return useQuery({
    queryKey: digitalHumanKeys.list(activeOrgId, params),
    queryFn: () => digitalHumansApi.list(params),
    enabled: options?.enabled ?? true,
    staleTime: 0,
    refetchOnMount: 'always',
    gcTime: 1000 * 60 * 10,
  });
}

export function useDigitalHuman(digitalHumanId: string | null, options?: { enabled?: boolean }) {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);

  return useQuery({
    queryKey: digitalHumanId
      ? digitalHumanKeys.detail(activeOrgId, digitalHumanId)
      : [...digitalHumanKeys.details(activeOrgId), 'disabled'],
    queryFn: () => {
      if (!digitalHumanId) throw new Error('Missing digitalHumanId');
      return digitalHumansApi.getById(digitalHumanId);
    },
    enabled: (options?.enabled ?? true) && !!digitalHumanId,
    staleTime: 0,
    refetchOnMount: 'always',
    gcTime: 1000 * 60 * 10,
  });
}

export function useCreateDigitalHuman() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DigitalHumanCreateRequest | DigitalHumanCreateFormData) =>
      digitalHumansApi.create(data),
    onSuccess: () => {
      invalidateDigitalHumans(queryClient);
    },
  });
}

export function useUpdateDigitalHuman() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      digitalHumanId,
      data,
      projectId,
    }: {
      digitalHumanId: string;
      data: DigitalHumanUpdateRequest | DigitalHumanUpdateFormData;
      projectId?: string | null;
    }) => digitalHumansApi.update(digitalHumanId, data, { projectId }),
    onSuccess: () => {
      invalidateDigitalHumans(queryClient);
    },
  });
}

export function useDeleteDigitalHuman() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: string | { digitalHumanId: string; projectId?: string | null }) => {
      if (typeof input === 'string') {
        return digitalHumansApi.delete(input);
      }
      return digitalHumansApi.delete(input.digitalHumanId, { projectId: input.projectId });
    },
    onSuccess: () => {
      invalidateDigitalHumans(queryClient);
    },
  });
}
